import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  AlertTitle,
  Card,
  CardContent,
  CardActions,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Tabs,
  Tab,
  FormControlLabel,
  Checkbox,
  FormHelperText,
  Snackbar,
  Collapse,
  LinearProgress,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import RefreshIcon from '@mui/icons-material/Refresh';
import BrainIcon from '@mui/icons-material/Psychology';
import AddIcon from '@mui/icons-material/Add';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import FolderIcon from '@mui/icons-material/Folder';
import { useForm, Controller } from 'react-hook-form';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ErrorMessage from '../../components/common/ErrorMessage';
import StatusCard from '../../components/common/StatusCard';
import knowledgeBaseApi from '../../api/knowledgeBaseApi';
import modelsApi from '../../api/modelsApi';
import crawlApi from '../../api/crawlApi';
import documentsApi from '../../api/documentsApi';
import databaseApi from '../../api/databaseApi';

const KnowledgeBasePage = () => {
  const [knowledgeBases, setKnowledgeBases] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isKBLoading, setIsKBLoading] = useState(true);
  const [isSourceLoading, setIsSourceLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [selectedKB, setSelectedKB] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [embeddingModels, setEmbeddingModels] = useState([]);
  const [isModelLoading, setIsModelLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(0);
  const [crawlJobs, setCrawlJobs] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [connections, setConnections] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedSources, setSelectedSources] = useState({
    crawls: [],
    documents: [],
    groups: [],
    database_connections: [],
    database_queries: []
  });
  const [openIndexDialog, setOpenIndexDialog] = useState(false);
  const [indexLoading, setIndexLoading] = useState(false);
  
  // 청크 크기 설정을 위한 상태 추가 (2025-10-07 업데이트: 품질 우선 + 효율성 개선)
  const [chunkSize, setChunkSize] = useState(1000);
  const [chunkOverlap, setChunkOverlap] = useState(200);  // 🔥 백엔드 기본값과 통일
  
  // 쿼리 목록을 위한 상태 추가
  const [queries, setQueries] = useState([]);
  
  // 인덱싱 진행 상황 관련 상태
  const [indexingInProgress, setIndexingInProgress] = useState(false);
  const [indexProgress, setIndexProgress] = useState(null);
  const [currentIndexId, setCurrentIndexId] = useState(null);
  
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: '',
    description: '',
    embedding_model: ''
  });
  
  const { control, handleSubmit, formState: { errors }, reset } = useForm({
    defaultValues: {
      name: '',
      description: '',
      embedding_model: '',
    }
  });

  const intervalRef = useRef(null);

  useEffect(() => {
    fetchKBs();
    fetchEmbeddingModels();
  }, []);

  const fetchKBs = async () => {
    try {
      setIsKBLoading(true);
      const response = await knowledgeBaseApi.listKnowledgeBases();
      setKnowledgeBases(response.knowledge_bases || []);
    } catch (error) {
      console.error('지식베이스 목록을 가져오는 중 오류 발생:', error);
      setError('지식베이스 목록을 불러올 수 없습니다.');
    } finally {
      setIsKBLoading(false);
    }
  };

  const fetchEmbeddingModels = async () => {
    try {
      setIsModelLoading(true);
      const response = await modelsApi.listEmbeddingModels();
      setEmbeddingModels(response.models || []);
    } catch (error) {
      console.error('임베딩 모델을 가져오는 중 오류 발생:', error);
      setError('임베딩 모델 목록을 불러올 수 없습니다.');
    } finally {
      setIsModelLoading(false);
    }
  };

  const fetchSourceData = async () => {
    try {
      setIsSourceLoading(true);
      
      // 각 API 호출을 개별적으로 처리하여 일부 실패해도 다른 데이터는 로드되도록 함
      const results = await Promise.allSettled([
        documentsApi.listDocuments(),
        crawlApi.listCrawls(),
        databaseApi.listConnections(),
        databaseApi.listQueries(),
        documentsApi.listGroups()
      ]);
      
      // 각 결과를 안전하게 처리
      const [documentsRes, crawlRes, connectionsRes, queriesRes, groupsRes] = results;
      
      // 문서 목록
      if (documentsRes.status === 'fulfilled') {
        const docsData = documentsRes.value;
        if (docsData && Array.isArray(docsData.documents)) {
          setDocuments(docsData.documents);
        } else if (Array.isArray(docsData)) {
          setDocuments(docsData);
        } else {
          console.warn('문서 데이터가 예상된 형식이 아닙니다:', docsData);
          setDocuments([]);
        }
      } else {
        console.error('문서 목록 로딩 실패:', documentsRes.reason);
        setDocuments([]);
      }
      
      // 크롤링 목록
      if (crawlRes.status === 'fulfilled') {
        const crawlData = crawlRes.value;
        // 응답이 배열인지 확인하고, 아니면 빈 배열로 설정
        if (Array.isArray(crawlData)) {
          setCrawlJobs(crawlData);
        } else if (crawlData && Array.isArray(crawlData.crawls)) {
          setCrawlJobs(crawlData.crawls);
        } else {
          console.warn('크롤링 데이터가 예상된 형식이 아닙니다:', crawlData);
          setCrawlJobs([]);
        }
      } else {
        console.error('크롤링 목록 로딩 실패:', crawlRes.reason);
        setCrawlJobs([]);
      }
      
      // 데이터베이스 연결 목록
      if (connectionsRes.status === 'fulfilled') {
        const connData = connectionsRes.value;
        if (connData && Array.isArray(connData.connections)) {
          setConnections(connData.connections);
        } else if (Array.isArray(connData)) {
          setConnections(connData);
        } else {
          console.warn('연결 데이터가 예상된 형식이 아닙니다:', connData);
          setConnections([]);
        }
      } else {
        console.error('데이터베이스 연결 목록 로딩 실패:', connectionsRes.reason);
        setConnections([]);
      }
      
      // 쿼리 목록
      if (queriesRes.status === 'fulfilled') {
        const queryData = queriesRes.value;
        if (queryData && Array.isArray(queryData.queries)) {
          setQueries(queryData.queries);
        } else if (Array.isArray(queryData)) {
          setQueries(queryData);
        } else {
          console.warn('쿼리 데이터가 예상된 형식이 아닙니다:', queryData);
          setQueries([]);
        }
      } else {
        console.warn('쿼리 목록 로딩 실패 (정상적일 수 있음):', queriesRes.reason);
        setQueries([]);
      }
      
      // 그룹 목록
      if (groupsRes.status === 'fulfilled') {
        const groupData = groupsRes.value;
        if (groupData && Array.isArray(groupData.groups)) {
          setGroups(groupData.groups);
        } else if (Array.isArray(groupData)) {
          setGroups(groupData);
        } else {
          console.warn('그룹 데이터가 예상된 형식이 아닙니다:', groupData);
          setGroups([]);
        }
      } else {
        console.error('그룹 목록 로딩 실패:', groupsRes.reason);
        setGroups([]);
      }
      
    } catch (error) {
      console.error('소스 데이터 로딩 오류:', error);
    } finally {
      setIsSourceLoading(false);
    }
  };

  // 지식베이스 선택 시 fetchSourceData 호출
  useEffect(() => {
    if (selectedKB) {
      fetchSourceData();
    }
  }, [selectedKB]);

  // 탭 변경 시에도 필요한 경우 데이터 갱신
  useEffect(() => {
    if (activeTab === 1 && selectedKB) {
      fetchSourceData();
    }
  }, [activeTab]);

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await knowledgeBaseApi.createKnowledgeBase(data);
      
      // 폼 초기화
      reset();
      
      // 지식베이스 목록 새로고침
      fetchKBs();
      
    } catch (error) {
      console.error('지식베이스 생성 오류:', error);
      setError('지식베이스를 생성하는 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedKB) return;
    
    try {
      await knowledgeBaseApi.deleteKnowledgeBase(selectedKB.kb_id);
      setKnowledgeBases(prevKBs => 
        prevKBs.filter(kb => kb.kb_id !== selectedKB.kb_id)
      );
      setOpenDeleteDialog(false);
      setSelectedKB(null);
    } catch (error) {
      console.error('지식베이스 삭제 중 오류 발생:', error);
      setError('지식베이스를 삭제할 수 없습니다.');
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleSourceToggle = (type, id) => {
    setSelectedSources(prev => {
      const current = prev[type];
      const isSelected = current.includes(id);
      
      if (type === 'groups') {
        // 그룹 선택/해제 시 개별 문서 선택도 함께 처리
        const groupDocuments = documents.filter(doc => doc.group_id === id).map(doc => doc.document_id);
        
        if (isSelected) {
          // 그룹 해제 시 해당 그룹의 문서들도 해제
          return {
            ...prev,
            groups: current.filter(item => item !== id),
            documents: prev.documents.filter(docId => !groupDocuments.includes(docId))
          };
        } else {
          // 그룹 선택 시 해당 그룹의 문서들도 선택
          return {
            ...prev,
            groups: [...current, id],
            documents: [...new Set([...prev.documents, ...groupDocuments])]
          };
        }
      } else if (type === 'documents') {
        // 개별 문서 선택/해제 시 그룹 상태도 확인
        const newDocuments = isSelected 
          ? current.filter(item => item !== id)
          : [...current, id];
        
        // 문서가 속한 그룹이 있는지 확인
        const document = documents.find(doc => doc.document_id === id);
        const groupId = document?.group_id;
        
        if (groupId) {
          const groupDocuments = documents.filter(doc => doc.group_id === groupId).map(doc => doc.document_id);
          const allGroupDocsSelected = groupDocuments.every(docId => 
            newDocuments.includes(docId) || docId === id && !isSelected
          );
          
          // 그룹의 모든 문서가 선택되었으면 그룹도 선택, 아니면 그룹 해제
          const newGroups = allGroupDocsSelected && !isSelected
            ? [...prev.groups, groupId]
            : prev.groups.filter(gId => gId !== groupId);
          
          return {
            ...prev,
            documents: newDocuments,
            groups: newGroups
          };
        } else {
          return {
            ...prev,
            documents: newDocuments
          };
        }
      } else {
        return {
          ...prev,
          [type]: isSelected 
            ? current.filter(item => item !== id)
            : [...current, id]
        };
      }
    });
  };

  // 인덱싱 진행 상황 폴링 함수
  const pollIndexProgress = useCallback(async () => {
    if (!selectedKB || !currentIndexId) {
      console.warn('폴링 중단: KB 또는 인덱스 ID가 없음');
      return;
    }
    
    try {
      console.log(`폴링: 인덱스 진행 상황 (${selectedKB.kb_id}/${currentIndexId})`);
      const progress = await knowledgeBaseApi.getIndexProgress(selectedKB.kb_id, currentIndexId);
      
      if (progress) {
        console.log(`인덱스 진행 상황: ${JSON.stringify(progress)}`);
        console.log(`진행률: ${progress.percent_complete}%, 단계: ${progress.current_stage}`);
        console.log(`문서: ${progress.document_count}/${progress.total_documents}, 임베딩: ${progress.embedding_count}/${progress.total_embeddings}`);
        
        // 진행 상태 업데이트
        setIndexProgress(prevProgress => {
          if (prevProgress) {
            console.log(`상태 변경: ${prevProgress.percent_complete}% → ${progress.percent_complete}%`);
          }
          return progress;
        });
        
        // 완료 또는 실패 상태 처리
        if (progress.status === 'completed' || progress.status === 'failed') {
          console.log(`인덱싱 종료: 상태=${progress.status}`);
          
          setIndexingInProgress(false);
          
          // 완료된 경우 지식베이스 목록 업데이트
          if (progress.status === 'completed') {
            fetchKBs(); // 지식베이스 목록 새로고침
          } else {
            // 실패 메시지 표시
            setError(`인덱스 생성 실패: ${progress.message}`);
          }
        }
      } else {
        console.warn("폴링: 응답에 진행 정보가 없음");
      }
    } catch (error) {
      console.error('폴링 중 오류:', error);
      // 폴링 중 오류가 발생해도 진행 상태를 초기화하지 않고 계속 시도
    }
  }, [currentIndexId, selectedKB, fetchKBs]);

  // 진행 상황 2초 간격 폴링 (실시간 진행률 업데이트)
  useEffect(() => {
    if (openIndexDialog && indexingInProgress && currentIndexId && selectedKB) {
      const poll = async () => {
        await pollIndexProgress();
      };
      poll();
      intervalRef.current = setInterval(poll, 2000);
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [openIndexDialog, indexingInProgress, currentIndexId, selectedKB, pollIndexProgress]);

  const handleCloseSuccess = () => {
    setSuccessMessage(null);
  };

  const handleCreateIndex = async () => {
    if (!selectedKB) return;
    
    try {
      setIndexLoading(true);
      setError(null);
      
      // 소스 데이터 형식 변환
      const formattedSources = {
        // 각 선택된 크롤링 ID에 대한 객체 생성
        crawl_sources: selectedSources.crawls.map(id => ({ crawl_id: id })),
        document_sources: selectedSources.documents.map(id => ({ document_id: id })),
        group_sources: selectedSources.groups.map(id => ({ group_id: id })),  // 그룹 소스 추가
        database_sources: selectedSources.database_connections.map(id => ({ connection_id: id })),
        database_query_sources: selectedSources.database_queries.map(id => ({ query_id: id }))
      };
      
      // 청크 설정 추가 - 사용자가 입력한 값 사용
      const indexData = {
        chunk_size: chunkSize,  // 사용자 설정값 사용
        chunk_overlap: chunkOverlap, // 사용자 설정값 사용
        ...formattedSources
      };
      
      console.log("Creating index with data:", JSON.stringify(indexData, null, 2));
      console.log("Selected KB:", selectedKB);
      console.log("API 요청 보내는 중: POST /knowledge-base/" + selectedKB.kb_id + "/index");
      
      try {
        const response = await knowledgeBaseApi.createIndex(selectedKB.kb_id, indexData);
        console.log("Index creation response:", response);
        
        // 인덱싱 진행 상황 추적을 위한 상태 설정
        if (response && response.success && response.data && response.data.index_id) {
          // 인덱스 ID 저장 및 인덱싱 상태 업데이트
          setCurrentIndexId(response.data.index_id);
          setIndexingInProgress(true);
          
          // 초기 진행 상태 설정
          setIndexProgress({
            status: "processing",
            message: response.message || "인덱싱 시작 중...",
            percent_complete: 0,
            current_stage: "준비 중",
            document_count: 0,
            embedding_count: 0,
            total_documents: 0,
            total_embeddings: 0
          });
          
          // 소스 선택 초기화
          setSelectedSources({
            crawls: [],
            documents: [],
            groups: [],
            database_connections: [],
            database_queries: []
          });
          
          console.log(`인덱싱 시작: ID=${response.data.index_id}`);
        } else {
          console.error("서버 응답에 index_id가 없습니다:", response);
          setError("서버 응답 오류: " + (response?.message || "알 수 없는 오류"));
          setIndexingInProgress(false);
        }
      } catch (apiError) {
        console.error("API 호출 실패:", apiError);
        console.error("API 오류 상세 정보:", apiError.response?.data || apiError.message);
        setError("API 호출 실패: " + (apiError.response?.data?.message || apiError.message));
        setIndexingInProgress(false);
      }
    } catch (error) {
      console.error('인덱스 생성 오류:', error);
      setError('인덱스를 생성하는 중 오류가 발생했습니다.');
      setIndexingInProgress(false);
    } finally {
      setIndexLoading(false);
    }
  };

// 지식베이스 데이터 소스에서 ID 목록을 안전하게 추출하는 헬퍼 함수
const getExistingIds = (sourcesList) => {
  if (!Array.isArray(sourcesList)) return [];
  return sourcesList.map(item => {
    if (typeof item === 'string') return item;
    return item.crawl_id || item.document_id || item.group_id || item.connection_id || item.query_id;
  }).filter(Boolean);
};

  // 인덱스 다이얼로그 열기 시 청크 크기 기본값으로 초기화 및 기존 등록 소스 사전 선택
  const handleOpenIndexDialog = (targetKB = null) => {
    const kb = (targetKB && typeof targetKB === 'object' && targetKB.kb_id) ? targetKB : selectedKB;
    if (kb && kb.kb_id) {
      setSelectedKB(kb);
    }

    setOpenIndexDialog(true);
    setChunkSize(1000); // 기본값으로 초기화 (품질 우선)
    setChunkOverlap(200); // 🔥 기본값으로 초기화 (20% 오버랩, 백엔드와 통일)
    fetchSourceData();

    // 🔥 이미 지식베이스에 인덱싱된 데이터 소스가 있다면 체크박스를 기본 선택 상태로 미리 세팅
    if (kb && kb.data_sources) {
      const ds = kb.data_sources;
      setSelectedSources({
        crawls: getExistingIds(ds.crawls),
        documents: getExistingIds(ds.documents),
        groups: getExistingIds(ds.groups),
        database_connections: getExistingIds(ds.database_connections),
        database_queries: getExistingIds(ds.database_queries)
      });
    } else {
      setSelectedSources({
        crawls: [],
        documents: [],
        groups: [],
        database_connections: [],
        database_queries: []
      });
    }
  };

  const handleEdit = (kb) => {
    setSelectedKB(kb);
    setOpenEditDialog(true);
    setEditFormData({
      name: kb.name,
      description: kb.description || '',
      embedding_model: kb.embedding_model
    });
  };

  const handleEditSubmit = async () => {
    if (!selectedKB) return;
    
    try {
      setIsLoading(true);
      setError(null);
      
      const response = await knowledgeBaseApi.updateKnowledgeBase(selectedKB.kb_id, editFormData);
      
      // 지식베이스 목록 새로고침
      fetchKBs();
      
      // 다이얼로그 닫기
      setOpenEditDialog(false);
      
      // 성공 메시지 표시
      setSuccessMessage('지식베이스가 성공적으로 업데이트되었습니다.');
    } catch (error) {
      console.error('지식베이스 수정 오류:', error);
      setError('지식베이스를 수정하는 중 오류가 발생했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        지식베이스 관리
      </Typography>
      
      {/* 성공 메시지 표시 */}
      <Collapse in={!!successMessage} sx={{ mb: 2 }}>
        <Alert 
          severity="success"
          action={
            <IconButton
              aria-label="close"
              color="inherit"
              size="small"
              onClick={handleCloseSuccess}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
          icon={<CheckCircleIcon fontSize="inherit" />}
          sx={{ mb: 2 }}
        >
          <AlertTitle>성공</AlertTitle>
          {successMessage}
        </Alert>
      </Collapse>
      
      <Paper elevation={3} sx={{ mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="지식베이스 생성" />
          <Tab 
            label="인덱스 구축" 
            disabled={!knowledgeBases.length}
          />
        </Tabs>
        
        {activeTab === 0 ? (
          <Box sx={{ p: 3 }}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    name="name"
                    control={control}
                    rules={{ required: '지식베이스 이름을 입력해주세요.' }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="지식베이스 이름"
                        fullWidth
                        variant="outlined"
                        error={!!errors.name}
                        helperText={errors.name?.message}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="설명 (선택사항)"
                        fullWidth
                        variant="outlined"
                        multiline
                        rows={2}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Controller
                    name="embedding_model"
                    control={control}
                    rules={{ required: '임베딩 모델을 선택해주세요.' }}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.embedding_model}>
                        <InputLabel>기본 임베딩 모델</InputLabel>
                        <Select
                          {...field}
                          label="기본 임베딩 모델"
                          disabled={isModelLoading}
                        >
                          {embeddingModels.map((model) => (
                            <MenuItem key={model.id} value={model.id}>
                              {model.name} - {model.provider}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.embedding_model && (
                          <FormHelperText>{errors.embedding_model.message}</FormHelperText>
                        )}
                      </FormControl>
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                    disabled={isLoading}
                    sx={{ mt: 2 }}
                  >
                    {isLoading ? <LoadingSpinner size={24} /> : '지식베이스 생성'}
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Box>
        ) : (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              인덱스 구축
            </Typography>
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>지식베이스 선택</InputLabel>
              <Select
                value={selectedKB ? selectedKB.kb_id : ''}
                onChange={(e) => {
                  const kb = knowledgeBases.find(kb => kb.kb_id === e.target.value);
                  setSelectedKB(kb || null);
                }}
                label="지식베이스 선택"
              >
                {knowledgeBases.map((kb) => (
                  <MenuItem key={kb.kb_id} value={kb.kb_id}>
                    {kb.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {selectedKB && (
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<BuildIcon />}
                  onClick={() => handleOpenIndexDialog()}
                >
                  인덱스 생성 시작
                </Button>
              </Box>
            )}
          </Box>
        )}
      </Paper>
      
      {/* 지식베이스 목록 */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            지식베이스 목록
          </Typography>
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchKBs}
            disabled={isKBLoading}
          >
            새로고침
          </Button>
        </Box>
        <Divider sx={{ mb: 2 }} />
        
        {isKBLoading ? (
          <LoadingSpinner message="지식베이스 목록을 불러오는 중..." />
        ) : error ? (
          <ErrorMessage message={error} />
        ) : knowledgeBases.length === 0 ? (
          <Alert severity="info">
            <AlertTitle>지식베이스 없음</AlertTitle>
            아직 생성된 지식베이스가 없습니다. 상단 탭에서 새 지식베이스를 생성해주세요.
          </Alert>
        ) : (
          <Grid container spacing={2}>
            {knowledgeBases.map((kb) => {
              const isBuilding = kb.status === 'building' || kb.status === 'processing';
              // 진행 중인 KB와 진행률 정보 매칭
              const isCurrentIndexing = isBuilding && indexProgress && kb.kb_id === selectedKB?.kb_id;
              const percent = isCurrentIndexing ? Math.round(indexProgress.percent_complete || 0) : null;
              return (
                <Grid item xs={12} key={kb.kb_id}>
                  <StatusCard
                    title={kb.name}
                    status={kb.status}
                    message={kb.description || '설명 없음'}
                    details={{
                      '지식베이스 ID': kb.kb_id,
                      '임베딩 모델': kb.embedding_model,
                      '생성 시간': new Date(kb.created_at).toLocaleString(),
                      '문서 수': kb.document_count || 0,
                      '마지막 업데이트': kb.last_updated ? new Date(kb.last_updated).toLocaleString() : '-',
                      // 🔥 데이터 소스 정보 표시
                      ...(kb.data_sources && {
                        '데이터 소스': (() => {
                          const lines = [];
                          
                          // 웹크롤링 상세 정보
                          if (kb.data_sources.crawls && kb.data_sources.crawls.length > 0) {
                            const crawlItems = kb.data_sources.crawls.map(c => 
                              `${c.url} (${c.page_count}페이지)`
                            );
                            lines.push(`• 웹크롤링: ${crawlItems.join(', ')}`);
                          }
                          
                          // 문서 상세 정보
                          if (kb.data_sources.documents && kb.data_sources.documents.length > 0) {
                            const docItems = kb.data_sources.documents.map(d => d.file_name);
                            // 문서가 많으면 처음 3개만 표시
                            const displayDocs = docItems.length > 3 
                              ? docItems.slice(0, 3).join(', ') + ` 외 ${docItems.length - 3}개`
                              : docItems.join(', ');
                            lines.push(`• 문서: ${displayDocs}`);
                          }
                          
                          // 문서그룹 상세 정보
                          if (kb.data_sources.groups && kb.data_sources.groups.length > 0) {
                            const groupItems = kb.data_sources.groups.map(g => 
                              `${g.group_name} (${g.document_count}개)`
                            );
                            lines.push(`• 문서그룹: ${groupItems.join(', ')}`);
                          }
                          
                          // DB 연결 정보
                          if (kb.data_sources.database_connection_count > 0) {
                            lines.push(`• DB연결: ${kb.data_sources.database_connection_count}개`);
                          }
                          
                          // DB 쿼리 정보
                          if (kb.data_sources.database_query_count > 0) {
                            lines.push(`• DB쿼리: ${kb.data_sources.database_query_count}개`);
                          }
                          
                          return lines.length > 0 ? lines.join('\n') : '없음';
                        })()
                      })
                    }}
                    isLoading={isBuilding}
                    progressPercent={percent}
                  />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -1, mb: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      startIcon={<BuildIcon />}
                      onClick={() => handleOpenIndexDialog(kb)}
                      disabled={kb.status === 'building' || kb.status === 'processing'}
                      sx={{ mr: 1 }}
                    >
                      인덱스 관리 / 소스 편집
                    </Button>
                    <Button
                      variant="outlined"
                      color="primary"
                      size="small"
                      onClick={() => handleEdit(kb)}
                      disabled={kb.status === 'building' || kb.status === 'processing'}
                      sx={{ mr: 1 }}
                    >
                      수정
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => {
                        setSelectedKB(kb);
                        setOpenDeleteDialog(true);
                      }}
                    >
                      삭제
                    </Button>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        )}
      </Paper>
      
      {/* 삭제 확인 다이얼로그 */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>지식베이스 삭제</DialogTitle>
        <DialogContent>
          <DialogContentText>
            "{selectedKB?.name}" 지식베이스를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>취소</Button>
          <Button onClick={handleDelete} color="error">삭제</Button>
        </DialogActions>
      </Dialog>
      
      {/* 인덱스 생성 다이얼로그 */}
      <Dialog 
        open={openIndexDialog} 
        onClose={() => {
          // 인덱싱 중에는 닫기 버튼을 눌러도 닫히지 않도록 설정
          if (!indexingInProgress) {
            setOpenIndexDialog(false);
          }
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>인덱스 생성</DialogTitle>
        <DialogContent>
          {indexingInProgress && indexProgress ? (
            // 인덱싱 진행 중 화면 표시
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                인덱스 생성 진행 중
              </Typography>
              
              {/* 진행 상태 표시바 */}
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
                <Box sx={{ width: '100%', mr: 1 }}>
                  <LinearProgress 
                    variant="determinate" 
                    value={indexProgress.percent_complete || 0} 
                    sx={{ height: 10, borderRadius: 5 }}
                  />
                </Box>
                <Box sx={{ minWidth: 35 }}>
                  <Typography variant="body2" color="text.secondary">
                    {`${Math.round(indexProgress.percent_complete || 0)}%`}
                  </Typography>
                </Box>
              </Box>
              
              {/* 상태 칩 표시 */}
              <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 2 }}>
                <Chip 
                  label={indexProgress.current_stage || "준비 중"} 
                  color={
                    indexProgress.status === 'completed' ? 'success' : 
                    indexProgress.status === 'failed' ? 'error' : 
                    'primary'
                  }
                  sx={{ mr: 1, mb: 1 }}
                />
                
                {indexProgress.document_count > 0 && indexProgress.total_documents > 0 && (
                  <Chip 
                    label={`문서 처리: ${indexProgress.document_count}/${indexProgress.total_documents}`} 
                    color="info"
                    sx={{ mr: 1, mb: 1 }}
                  />
                )}
                
                {indexProgress.embedding_count > 0 && indexProgress.total_embeddings > 0 && (
                  <Chip 
                    label={`임베딩 생성: ${indexProgress.embedding_count}/${indexProgress.total_embeddings}`} 
                    color="info"
                    sx={{ mr: 1, mb: 1 }}
                  />
                )}
              </Box>
              
              {/* 상세 진행 정보 */}
              <Box sx={{ mt: 2, bgcolor: 'background.paper', p: 2, borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>진행 상태:</Typography>
                <Typography variant="body2">
                  {indexProgress.message && indexProgress.message !== "Indexing process started" 
                    ? indexProgress.message 
                    : `${indexProgress.current_stage || "인덱싱 진행 중..."}${indexProgress.percent_complete > 0 ? ` (${indexProgress.percent_complete}%)` : ""}`}
                </Typography>
                
                {indexProgress.current_stage && (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>현재 단계:</Typography>
                    <Typography variant="body2">{indexProgress.current_stage}</Typography>
                  </>
                )}
                
                {indexProgress.document_count >= 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>문서 처리:</Typography>
                    <Typography variant="body2">
                      {indexProgress.document_count} / {indexProgress.total_documents || '?'} 
                      {indexProgress.total_documents > 0 ? 
                        ` (${Math.round((indexProgress.document_count / indexProgress.total_documents) * 100)}%)` : 
                        ''}
                    </Typography>
                  </>
                )}
                
                {indexProgress.embedding_count >= 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ mt: 1 }}>임베딩 생성:</Typography>
                    <Typography variant="body2">
                      {indexProgress.embedding_count} / {indexProgress.total_embeddings || '?'}
                      {indexProgress.total_embeddings > 0 ? 
                        ` (${Math.round((indexProgress.embedding_count / indexProgress.total_embeddings) * 100)}%)` : 
                        ''}
                    </Typography>
                  </>
                )}
              </Box>
              
              {/* 오류 메시지 표시 */}
              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}
              
              {/* 성공 메시지 표시 */}
              {successMessage && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  {successMessage}
                </Alert>
              )}
            </Box>
          ) : (
            // 일반 인덱스 생성 폼 표시
            (() => {
              const existingCrawls = getExistingIds(selectedKB?.data_sources?.crawls);
              const existingDocs = getExistingIds(selectedKB?.data_sources?.documents);
              const existingGroups = getExistingIds(selectedKB?.data_sources?.groups);
              const existingConns = getExistingIds(selectedKB?.data_sources?.database_connections);
              const existingQueries = getExistingIds(selectedKB?.data_sources?.database_queries);

              return (
                <>
                  <Alert severity="info" sx={{ mb: 2 }}>
                    💡 <b>[이미 포함됨]</b> 표시가 붙은 항목은 현재 지식베이스({selectedKB?.name || ''})에 이미 저장되어 있는 소스입니다.<br />
                    • <b>데이터 추가하기</b>: 새로 덧붙이고 싶은 소스 항목을 추가로 체크하세요.<br />
                    • <b>데이터 제외하기</b>: 빼고 싶은 기존 소스의 체크박스를 해제(Uncheck)하고 <b>[인덱스 생성]</b>을 클릭하시면 변경사항이 반영됩니다.
                  </Alert>

                  {/* 에러 메시지 표시 */}
                  {error && (
                    <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                      {error}
                    </Alert>
                  )}
                  
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="h6">청크 설정</Typography>
                    <Grid container spacing={2} sx={{ mb: 3, mt: 1 }}>
                      <Grid item xs={6}>
                        <TextField
                          label="청크 크기"
                          type="number"
                          fullWidth
                          value={chunkSize}
                          onChange={(e) => setChunkSize(Number(e.target.value))}
                          InputProps={{
                            inputProps: { min: 100, max: 8000 }
                          }}
                          helperText="단일 청크의 최대 토큰 수 (100-8000)"
                        />
                      </Grid>
                      <Grid item xs={6}>
                        <TextField
                          label="청크 오버랩"
                          type="number"
                          fullWidth
                          value={chunkOverlap}
                          onChange={(e) => setChunkOverlap(Number(e.target.value))}
                          InputProps={{
                            inputProps: { min: 0, max: 1000 }
                          }}
                          helperText="인접 청크 간 중복 토큰 수 (0-1000)"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                  
                  {/* 소스 데이터 섹션 */}
                  <Typography variant="h6" gutterBottom>
                    크롤링 데이터
                  </Typography>
                  
                  {crawlJobs.length === 0 ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      사용 가능한 크롤링 작업이 없습니다.
                    </Alert>
                  ) : (
                    <List dense sx={{ mb: 2 }}>
                      {Array.isArray(crawlJobs) && crawlJobs.map((job) => (
                        <ListItem key={job.crawl_id}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedSources.crawls.includes(job.crawl_id)}
                                onChange={() => handleSourceToggle('crawls', job.crawl_id)}
                                disabled={job.status === 'crawling' && (!job.text_file_count || job.text_file_count === 0)}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2">
                                  URL: {job.url}
                                  {existingCrawls.includes(job.crawl_id) && (
                                    <Chip 
                                      size="small" 
                                      label="이미 포함됨" 
                                      color="primary" 
                                      sx={{ ml: 1, fontWeight: 'bold' }}
                                    />
                                  )}
                                  {job.status === 'crawling' && (
                                    <Chip 
                                      size="small" 
                                      label="크롤링 중" 
                                      color="warning" 
                                      sx={{ ml: 1 }}
                                    />
                                  )}
                                  {job.status === 'completed' && (
                                    <Chip 
                                      size="small" 
                                      label="완료됨" 
                                      color="success" 
                                      sx={{ ml: 1 }}
                                    />
                                  )}
                                  {job.status === 'cancelled' && (
                                    <Chip 
                                      size="small" 
                                      label="취소됨" 
                                      color="error" 
                                      sx={{ ml: 1 }}
                                    />
                                  )}
                                  {job.status === 'failed' && (
                                    <Chip 
                                      size="small" 
                                      label="실패" 
                                      color="error" 
                                      sx={{ ml: 1 }}
                                    />
                                  )}
                                  {job.text_file_count > 0 && (
                                    <Chip 
                                      size="small" 
                                      label={`${job.text_file_count}개의 텍스트 파일`}
                                      color="info" 
                                      sx={{ ml: 1 }}
                                    />
                                  )}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  크롤링 ID: {job.crawl_id}
                                </Typography>
                                <Typography variant="caption" display="block" color="text.secondary">
                                  페이지 수: {job.pages_crawled || 0}, 파일 수: {job.files_extracted || 0}
                                  {job.status === 'crawling' && ' (진행 중)'}
                                  {job.text_file_count > 0 && ` (사용 가능한 텍스트 파일: ${job.text_file_count}개)`}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                  
                  <Typography variant="h6" gutterBottom>
                    문서 그룹
                  </Typography>
                  
                  {groups.length === 0 ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      사용 가능한 문서 그룹이 없습니다.
                    </Alert>
                  ) : (
                    <List dense sx={{ mb: 2 }}>
                      {Array.isArray(groups) && groups.map((group) => (
                        <ListItem key={group.group_id}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedSources.groups.includes(group.group_id)}
                                onChange={() => handleSourceToggle('groups', group.group_id)}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <FolderIcon fontSize="small" color="primary" />
                                  {group.name}
                                  {existingGroups.includes(group.group_id) && (
                                    <Chip 
                                      size="small" 
                                      label="이미 포함됨" 
                                      color="primary" 
                                      sx={{ fontWeight: 'bold' }}
                                    />
                                  )}
                                  <Chip 
                                    size="small" 
                                    label={`${group.document_count}개 문서`}
                                    color="primary" 
                                    variant="outlined"
                                  />
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {group.description || '설명 없음'}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}

                  <Typography variant="h6" gutterBottom>
                    개별 문서
                  </Typography>
                  
                  {documents.length === 0 ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      사용 가능한 문서가 없습니다.
                    </Alert>
                  ) : (
                    <List dense sx={{ mb: 2 }}>
                      {Array.isArray(documents) && documents.map((doc) => (
                        <ListItem 
                          key={doc.document_id}
                          sx={{ 
                            backgroundColor: doc.group_id ? 'rgba(0, 0, 0, 0.02)' : 'transparent',
                            borderLeft: doc.group_id ? '3px solid #1976d2' : 'none',
                            pl: doc.group_id ? 2 : 3
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedSources.documents.includes(doc.document_id)}
                                onChange={() => handleSourceToggle('documents', doc.document_id)}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2">
                                  {doc.filename}
                                  {existingDocs.includes(doc.document_id) && (
                                    <Chip 
                                      size="small" 
                                      label="이미 포함됨" 
                                      color="primary" 
                                      sx={{ ml: 1, fontWeight: 'bold' }}
                                    />
                                  )}
                                  {doc.group_name && (
                                    <Chip 
                                      size="small" 
                                      label={doc.group_name}
                                      color="primary" 
                                      variant="outlined"
                                      sx={{ ml: 1 }}
                                    />
                                  )}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {doc.description || '설명 없음'}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                  
                  <Typography variant="h6" gutterBottom>
                    데이터베이스 연결
                  </Typography>
                  
                  {Array.isArray(connections) && connections.length === 0 ? (
                    <Alert severity="info">
                      사용 가능한 데이터베이스 연결이 없습니다.
                    </Alert>
                  ) : (
                    <List dense>
                      {Array.isArray(connections) && connections.map((conn) => (
                        <ListItem key={conn.connection_id}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedSources.database_connections.includes(conn.connection_id)}
                                onChange={() => handleSourceToggle('database_connections', conn.connection_id)}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2">
                                  {conn.name}
                                  {existingConns.includes(conn.connection_id) && (
                                    <Chip 
                                      size="small" 
                                      label="이미 포함됨" 
                                      color="primary" 
                                      sx={{ ml: 1, fontWeight: 'bold' }}
                                    />
                                  )}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {conn.db_type} - {conn.host}:{conn.port}/{conn.database}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}

                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    데이터베이스 쿼리 결과
                  </Typography>
                  
                  {queries.length === 0 ? (
                    <Alert severity="info">
                      사용 가능한 데이터베이스 쿼리 결과가 없습니다. 먼저 데이터베이스 관리 화면에서 쿼리를 실행하세요.
                    </Alert>
                  ) : (
                    <List dense>
                      {Array.isArray(queries) && queries.map((query) => (
                        <ListItem key={query.query_id}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={selectedSources.database_queries.includes(query.query_id)}
                                onChange={() => handleSourceToggle('database_queries', query.query_id)}
                              />
                            }
                            label={
                              <Box>
                                <Typography variant="body2">
                                  {query.query_text?.slice(0, 50)}{query.query_text?.length > 50 ? '...' : ''}
                                  {existingQueries.includes(query.query_id) && (
                                    <Chip 
                                      size="small" 
                                      label="이미 포함됨" 
                                      color="primary" 
                                      sx={{ ml: 1, fontWeight: 'bold' }}
                                    />
                                  )}
                                  <Chip 
                                    size="small" 
                                    label={`${query.row_count || 0}개 행`}
                                    color="primary" 
                                    sx={{ ml: 1 }}
                                  />
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {query.connection_name} ({query.db_type}/{query.database})
                                </Typography>
                                {query.columns && query.columns.length > 0 && (
                                  <Typography variant="caption" display="block" color="text.secondary">
                                    컬럼: {query.columns.join(', ')}
                                  </Typography>
                                )}
                              </Box>
                            }
                          />
                        </ListItem>
                      ))}
                    </List>
                  )}
                </>
              );
            })()
          )}
        </DialogContent>
        <DialogActions>
          {indexingInProgress ? (
            // 인덱싱 진행 중일 때 닫기 버튼 비활성화
            <Button 
              onClick={() => setOpenIndexDialog(false)}
              disabled={true}
              color="primary"
            >
              닫기
            </Button>
          ) : (
            // 인덱싱 진행 중이 아닐 때 정상 버튼 표시
            <>
              <Button 
                onClick={() => setOpenIndexDialog(false)}
                disabled={indexLoading}
              >
                취소
              </Button>
              <Button 
                onClick={handleCreateIndex}
                color="primary"
                variant="contained"
                disabled={
                  indexLoading || 
                  !selectedKB || 
                  (
                    selectedSources.crawls.length === 0 && 
                    selectedSources.documents.length === 0 && 
                    selectedSources.groups.length === 0 &&  // 그룹 선택도 확인
                    selectedSources.database_connections.length === 0 &&
                    selectedSources.database_queries.length === 0
                  )
                }
              >
                {indexLoading ? <LoadingSpinner size={24} /> : '인덱스 생성'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
      
      {/* 지식베이스 수정 다이얼로그 */}
      <Dialog
        open={openEditDialog}
        onClose={() => setOpenEditDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>지식베이스 수정</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <TextField
                  label="지식베이스 이름"
                  fullWidth
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                  required
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  label="설명 (선택사항)"
                  fullWidth
                  value={editFormData.description}
                  onChange={(e) => setEditFormData({...editFormData, description: e.target.value})}
                  multiline
                  rows={2}
                />
              </Grid>
              
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>임베딩 모델</InputLabel>
                  <Select
                    value={editFormData.embedding_model}
                    onChange={(e) => setEditFormData({...editFormData, embedding_model: e.target.value})}
                    label="임베딩 모델"
                    disabled={isModelLoading}
                  >
                    {embeddingModels.map((model) => (
                      <MenuItem key={model.id} value={model.id}>
                        {model.name} - {model.provider}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenEditDialog(false)}
            disabled={isLoading}
          >
            취소
          </Button>
          <Button 
            onClick={handleEditSubmit}
            color="primary"
            variant="contained"
            disabled={isLoading || !editFormData.name || !editFormData.embedding_model}
          >
            {isLoading ? <LoadingSpinner size={24} /> : '저장'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default KnowledgeBasePage;